import { useMemo } from 'react';
import { Dictionary } from 'lodash';
import { TimeRange, DataRange } from '../../ui/chart/types';
import { TimeSeries, NumericDataPoint } from '../../../models';
import { ChartGroups } from './types';

const getMin = (points: NumericDataPoint[]) => {
  return points.reduce((minSoFar, point) => {
    return Math.min(minSoFar, point.value === null ? Infinity : point.value);
  }, Infinity);
};

const getMax = (points: NumericDataPoint[]) => {
  return points.reduce((maxSoFar, point) => {
    return Math.max(maxSoFar, point.value === null ? -Infinity : point.value);
  }, -Infinity);
};

export interface RangeGroups {
  chart: Dictionary<DataRange>;
  bitfield: DataRange[];
  enum: DataRange[];
}

const getRanges = (
  tagIds: string[],
  timeSeries: Dictionary<TimeSeries>,
  timeRange: TimeRange,
  paddingPer?: number
): RangeGroups => {
  const groups: Dictionary<{ mins: Dictionary<number>; maxes: Dictionary<number> }> = {
    chart: { mins: {}, maxes: {} },
    bitfield: { mins: {}, maxes: {} },
    enum: { mins: {}, maxes: {} },
  };

  tagIds.forEach((tagId) => {
    const series = timeSeries[tagId];
    const unit = series.unit;
    let chartGroup: keyof ChartGroups = 'chart';

    if (series.dataType === 'Bitfield') {
      chartGroup = 'bitfield';
    }
    if (series.dataType === 'Enum') {
      chartGroup = 'enum';
    }

    const seriesMin = getMin(series.points);
    const seriesMax = getMax(series.points);

    groups[chartGroup].mins[unit] = Math.min(seriesMin, groups[chartGroup].mins[unit] || Infinity);
    groups[chartGroup].maxes[unit] = Math.max(
      seriesMax,
      groups[chartGroup].maxes[unit] || -Infinity
    );
  });

  const result: RangeGroups = {
    chart: {},
    bitfield: [],
    enum: [],
  };

  Object.entries(groups).forEach(([groupKey, group]) => {
    if (groupKey === 'chart') {
      const ranges: Dictionary<DataRange> = {};
      Object.entries(group.mins).forEach(([unit, yMin]) => {
        const yMax = group.maxes[unit];
        const range = {
          xMin: timeRange.start,
          xMax: timeRange.end,
          yMin: yMin === Infinity ? 0 : yMin,
          yMax: yMax === -Infinity ? 0 : yMax,
        };

        if (paddingPer) {
          const padding = (range.yMax - range.yMin || 1) * paddingPer;
          ranges[unit] = {
            ...range,
            yMin: range.yMin - padding,
            yMax: range.yMax + padding,
          };
        } else {
          ranges[unit] = range;
        }
      });
      result.chart = ranges;
      return;
    }

    const ranges = Object.keys(group.mins).map((unit) => {
      const range = {
        xMin: timeRange.start,
        xMax: timeRange.end,
        yMin: group.mins[unit] === Infinity ? 0 : group.mins[unit],
        yMax: group.maxes[unit] === -Infinity ? 0 : group.maxes[unit],
      };

      if (paddingPer) {
        const padding = (range.yMax - range.yMin || 1) * paddingPer;
        return {
          ...range,
          yMin: range.yMin - padding,
          yMax: range.yMax + padding,
        };
      }
      return range;
    });

    result[groupKey as 'bitfield' | 'enum'] = ranges;
  });

  return result;
};

export function useRanges(
  tagIds: string[],
  snapshots: Dictionary<TimeSeries>,
  xRange: TimeRange,
  zoomRange?: TimeRange
) {
  const reversedTagIds = useMemo(() => tagIds.slice().reverse(), [tagIds]);

  const ranges = useMemo(
    () => getRanges(reversedTagIds, snapshots, zoomRange || xRange, 0.05),
    [reversedTagIds, snapshots, xRange, zoomRange]
  );

  const fullRanges = useMemo(
    () => getRanges(reversedTagIds, snapshots, xRange, 0.05),
    [reversedTagIds, snapshots, xRange]
  );

  return { ranges, fullRanges, reversedTagIds };
}
